import { create } from "zustand";

import type { AlbumLengthFormat, BoardPattern } from "@/settings/types/types";

export const ALBUM_LENGTH_FORMAT_STORAGE_KEY = "ceedero.albumLengthFormat";

export const DEFAULT_ALBUM_LENGTH_FORMAT: AlbumLengthFormat = "minutes";

const ALBUM_LENGTH_FORMATS = new Set<string>(["hours", "minutes"]);

const isAlbumLengthFormat = (value: unknown): value is AlbumLengthFormat =>
  typeof value === "string" && ALBUM_LENGTH_FORMATS.has(value);

/**
 * The stored preference, or the application's own default.
 *
 * Belongs to this browser rather than to the account, like the theme and the
 * language beside it: somebody signing in on a borrowed machine gets that
 * machine's preferences, and none of this is sent anywhere.
 */
const readStoredFormat = () => {
  if (typeof localStorage === "undefined") {
    return DEFAULT_ALBUM_LENGTH_FORMAT;
  }
  const stored: unknown = localStorage.getItem(ALBUM_LENGTH_FORMAT_STORAGE_KEY);
  return isAlbumLengthFormat(stored) ? stored : DEFAULT_ALBUM_LENGTH_FORMAT;
};

export const BOARD_PATTERN_STORAGE_KEY = "ceedero.boardPattern";

/** Dots, as on the desktop, whose default this is. */
export const DEFAULT_BOARD_PATTERN: BoardPattern = "dots";

const isBoardPattern = (value: unknown): value is BoardPattern =>
  value === "dots" || value === "lines";

const readStoredBoardPattern = () => {
  if (typeof localStorage === "undefined") {
    return DEFAULT_BOARD_PATTERN;
  }
  const stored: unknown = localStorage.getItem(BOARD_PATTERN_STORAGE_KEY);
  return isBoardPattern(stored) ? stored : DEFAULT_BOARD_PATTERN;
};

interface DisplaySettingsState {
  albumLengthFormat: AlbumLengthFormat;
  boardPattern: BoardPattern;
  setAlbumLengthFormat: (albumLengthFormat: AlbumLengthFormat) => void;
  setBoardPattern: (boardPattern: BoardPattern) => void;
}

export const useDisplaySettingsStore = create<DisplaySettingsState>((set) => ({
  albumLengthFormat: readStoredFormat(),
  boardPattern: readStoredBoardPattern(),
  setAlbumLengthFormat: (albumLengthFormat) => {
    set({ albumLengthFormat });
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(ALBUM_LENGTH_FORMAT_STORAGE_KEY, albumLengthFormat);
    }
  },
  setBoardPattern: (boardPattern) => {
    set({ boardPattern });
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(BOARD_PATTERN_STORAGE_KEY, boardPattern);
    }
  },
}));
